import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment-timezone';
import CalendarAtom from '../../atoms/Calendar/CalendarAtom';
import TimePickerAtom from '../../atoms/TimePicker/TimePickerAtom';
import DateTimeDisplayAtom from '../../atoms/DateTimeDisplay/DateTimeDisplayAtom';
import './DateTimePickerMolecule.scss';

export interface DateTimeValue {
  date: Date | null; // JS Date (we ignore its time)
  time: string;      // "HH:mm"
  timezone: string;  // IANA
}

export interface DateTimePickerMoleculeProps {
  /** optional Formik name (e.g. "properties.start") */
  name?: string;
  /** optional Formik blur handler */
  onBlur?: React.FocusEventHandler<any>;

  value?: DateTimeValue;
  onChange?: (value: DateTimeValue) => void;
  label?: string;
  dateFormat?: string;
  timezoneOptions?: string[];
  defaultTimezone?: string;
  className?: string;
  errors?: {
    date?: string[];
    time?: string[];
  };
  showPreview?: boolean;
}

export const DateTimePickerMolecule: React.FC<DateTimePickerMoleculeProps> = ({
  name,
  onBlur,
  value,
  onChange,
  label,
  dateFormat = 'mm/dd/yy',
  timezoneOptions,
  defaultTimezone = moment.tz.guess(),
  className = '',
  errors = {},
  showPreview = false,
}) => {
  // Normalize incoming date to midnight
  const parseInitial = (val?: DateTimeValue): DateTimeValue => {
    if (!val) {
      const today = moment().startOf('day').toDate();
      return { date: today, time: '00:00', timezone: defaultTimezone };
    }
    const { date, time, timezone } = val;
    const normalizedDate = date
      ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
      : null;
    return {
      date: normalizedDate,
      time: time || '00:00',
      timezone: timezone || defaultTimezone,
    };
  };

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    () => parseInitial(value).date
  );
  const [selectedTime, setSelectedTime] = useState<string>(
    () => parseInitial(value).time
  );
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    () => parseInitial(value).timezone
  );

  // Sync when `value` changes
  // Note: We intentionally don't include defaultTimezone in dependencies
  // to prevent timezone from reverting when user selects a different one
  useEffect(() => {
    if (value) {
      const { date, time, timezone } = value;
      const normalizedDate = date
        ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
        : null;
      setSelectedDate(normalizedDate);
      setSelectedTime(time || '00:00');
      setSelectedTimezone(timezone || defaultTimezone);
    } else {
      const init = parseInitial(value);
      setSelectedDate(init.date);
      setSelectedTime(init.time);
      setSelectedTimezone(init.timezone);
    }
  }, [value]); // Remove defaultTimezone from dependencies

  // Handle calendar date changes (zero out time on day‑change)
  const handleDateChange = useCallback(
    (d: Date | Date[] | null) => {
      // CalendarAtom can return arrays for multiple/range selection, but we're using single mode
      const singleDate = Array.isArray(d) ? d[0] || null : d;
      
      console.log('[DateTimePickerMolecule] (handleDateChange) Date changed:', singleDate);
      const normalized = singleDate
        ? new Date(singleDate.getFullYear(), singleDate.getMonth(), singleDate.getDate())
        : null;
      console.log('[DateTimePickerMolecule] Normalized date:', normalized);
      const isDifferentDay =
        normalized &&
        (!selectedDate || normalized.getTime() !== selectedDate.getTime());
      console.log('[DateTimePickerMolecule] Is different day:', isDifferentDay);
      const newTime = isDifferentDay ? '00:00' : selectedTime;
      console.log('[DateTimePickerMolecule] New time:', newTime);

      setSelectedDate(normalized);
      setSelectedTime(newTime);
      onChange?.({ date: normalized, time: newTime, timezone: selectedTimezone });
    },
    [selectedDate, selectedTime, selectedTimezone, onChange]
  );

  // Handle time wheel changes
  const handleTimeChange = useCallback(
    (t: string) => {
      setSelectedTime(t);
      console.log('[DateTimePickerMolecule] Time changed:', t);
      console.log('[DateTimePickerMolecule] Current date:', selectedDate);
      console.log('[DateTimePickerMolecule] Current timezone:', selectedTimezone);
      console.log('[DateTimePickerMolecule] Notifying parent...');
      onChange?.({ date: selectedDate, time: t, timezone: selectedTimezone });
    },
    [selectedDate, selectedTimezone, onChange]
  );

  // Handle timezone select
  const handleTZChange = useCallback(
    (tz: string) => {
      setSelectedTimezone(tz);
      onChange?.({ date: selectedDate, time: selectedTime, timezone: tz });
    },
    [selectedDate, selectedTime, onChange]
  );

  return (
    <div className={`date-time-picker-molecule ${className}`}>
      {label && <h3 className="date-time-picker-molecule__label">{label}</h3>}

      <div className="date-time-picker-molecule__controls">
        <div className="date-time-picker-molecule__date">
          <CalendarAtom
            name={name ? `${name}.date` : undefined}
            onBlur={onBlur}
            value={selectedDate}
            onDateChange={handleDateChange}
            inline
            dateFormat={dateFormat}
            
            errors={errors.date}
          />
        </div>

        <div className="date-time-picker-molecule__time-zone">
          <TimePickerAtom
            name={name ? `${name}.time` : undefined}
            onBlur={onBlur}
            baseDate={selectedDate}
            value={selectedTime}
            onChange={handleTimeChange}
            zone={selectedTimezone}
            zoneOptions={timezoneOptions}
            onZoneChange={handleTZChange}
            errors={errors.time}
            className="date-time-picker-molecule__time-input"
          />
        </div>
      </div>

      {showPreview && (selectedDate || selectedTime) && (
        <DateTimeDisplayAtom
          date={selectedDate}
          time={selectedTime}
          timezone={selectedTimezone}
          className="date-time-picker-molecule__preview"
        />
      )}
    </div>
  );
};

export default DateTimePickerMolecule;